import React, { useState, useEffect } from "react"
import styled from "styled-components"
import { GatsbyImage } from "gatsby-plugin-image"
import { withTranslation } from "gatsby-plugin-react-i18next"
import { Link } from "gatsby"

import breakpoints from "../../../styles/breakpoints"
import fonts from "../../../styles/fonts"
import colors from "../../../styles/colors"

import OutOfStockSticker from "../../Core/OutOfStockSticker"
import DiscountSticker from "../../Core/DiscountSticker"
import MessageSticker from "../../Core/MessageSticker"

import {
  isPackshotImage,
  isVariantImagePackshot,
} from "../../../services/images"
import {
  getEngagementLink,
  getProductLinkByMaterial,
} from "../../../services/links"
import { getStartingPrice } from "../../../services/engagementRingHelper"
import {
  getGlobalOutOfStock,
  getLocalisedPrice,
  getPreorderStatus,
} from "../../../services/productHelper"
import { getIsFrance } from "../../../services/locale"

import YellowIcon from "../../../resources/img/ui/swatches/yellow-gold.svg"
import PinkIcon from "../../../resources/img/ui/swatches/rose-gold.svg"
import WhiteIcon from "../../../resources/img/ui/swatches/white-gold.svg"
import Platinum from "../../../resources/img/ui/swatches/platinum-engagement.svg"
// import RoseWhiteIcon from "../../../resources/img/ui/swatches/rose-gold-white-gold.png"
import RoseYellowIcon from "../../../resources/img/ui/swatches/rose-gold-yellow-gold.png"
import WhiteRoseIcon from "../../../resources/img/ui/swatches/white-gold-rose-gold.png"
import WhiteYellowIcon from "../../../resources/img/ui/swatches/white-gold-yellow-gold.png"
import YellowRoseIcon from "../../../resources/img/ui/swatches/yellow-gold-rose-gold.png"
import YellowWhiteIcon from "../../../resources/img/ui/swatches/yellow-gold-white-gold.png"

const ProductDetailsBlock = styled.div`
  margin-top: 10px;
`

const CardContainer = styled.div`
  display: grid;
  grid-template-rows: 1fr auto;

  .gatsby-image-wrapper [data-main-image] {
    transition: opacity 0s !important;
  }

  div [data-placeholder-image] {
    display: none !important;
  }

  @media (min-width: ${breakpoints.md}) {
    grid-template-rows: ${(props) =>
      props.$regular ? "auto 1fr" : "1fr auto"};
    min-height: 100%;
  }
`

const ProductDetails = styled.div`
  ${fonts.openSans};
  font-size: 10px;
  line-height: 18px;
  text-transform: uppercase;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  @media (min-width: ${breakpoints.md}) {
    font-size: 14px;
    line-height: 24px;
  }
`

const TitleSection = styled.div`
  padding-right: 8px;
  flex: 1;
`

const Title = styled.div`
  ${fonts.openSans};
  font-weight: 600;
  color: ${colors.mediumTextGrey};
`

const Price = styled.div`
  ${fonts.openSans};
  font-weight: ${(props) => (props.$crossed ? 300 : props.sale ? 600 : 400)};
  color: ${(props) =>
    props.$crossed ? colors.mediumDarkGrey : colors.mediumTextGrey};
  margin-right: 4px;
  font-style: ${(props) => (props.$crossed ? "italic" : "none")};
  position: relative;

  span {
    font-weight: 400;
    font-size: 10px;
    text-transform: none;
  }

  @media (min-width: ${breakpoints.md}) {
    margin-right: 4px;

    span {
      font-size: 12px;
    }
  }
`

const ImageContainer = styled.div`
  background: ${colors.white};
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: ${(props) => (props.wide ? "absolute" : "relative")};

  .gatsby-image-wrapper {
    height: 100%;
  }

  img {
    object-fit: contain !important;
    width: 100%;
    height: 100%;
  }
`

// const ProductGrid = styled.div`
//   display: grid;
//   grid-template-columns: repeat(3, 1fr);
//   grid-gap: 20px;
//   justify-items: center;
//   width: 100%;
// `;

// const ProductCardContainer = styled.div`
//   background: ${colors.white};
//   width: 100%;
//   height: 100%;
//   max-width: 312px; // Adjust as needed for card size
//   min-height: 400px; // Set a fixed min-height for the cards
//   display: flex;
//   flex-direction: column;
//   justify-content: space-between;
//   margin: auto;

//   a {
//     color: inherit;
//     text-decoration: none;
//   }
// `;

const StyledImage = styled(GatsbyImage)`
  opacity: ${(props) => (props.$visible ? 1 : 0)};
`

const AlternativeImage = styled(StyledImage)`
  position: absolute !important;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;

  img {
    object-fit: cover !important;
  }
`

const VariantImage = styled(StyledImage)`
  position: absolute !important;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  opacity: 1;

  img {
    object-fit: contain !important;
    width: 100%;
    height: 100%;
  }
`

const Mobile = styled.div`
  position: relative;
  background: ${colors.white};
  @media (min-width: ${breakpoints.md}) {
    display: none;
  }
`

const Desktop = styled.div`
  display: none;
  position: relative;
  @media (min-width: ${breakpoints.md}) {
    display: block;
  }
`

const WideImageContainer = styled.div`
  position: relative;
  padding-bottom: calc(((100% - 34px) / 2) / 0.82835820895);
`

const ProductImageLink = styled(Link)`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 1;
`

const IconContainer = styled.div`
  display: inline-block;
  cursor: pointer;
  height: 18px;
  width: 18px;
  margin: 0 3px;
  position: relative;
  border: ${(props) =>
    props.$isSelected ? `1px solid ${colors.mediumTextGrey}` : "none"};
  border-radius: 50%;
  img {
    height: 12px;
    width: 12px;
    position: absolute;
    top: calc(50% - 6px);
    left: calc(50% - 6px);
  }

  @media (min-width: ${breakpoints.md}) {
    margin: 0 3px;
  }
`

const OriginalPrice = styled.div`
  display: inline;
  position: relative;
`

const Icons = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
`

const PriceSection = styled.div`
  display: flex;
  align-items: center;

  display: ${(props) => (props.$desktop ? "none" : "flex")};
  min-width: 100%;
  @media (min-width: ${breakpoints.md}) {
    display: ${(props) => (props.$desktop ? "flex" : "none")};
    min-width: unset;
  }
`

const Line = styled.div`
  width: 100%;
  position: absolute;
  height: 1px;
  background: ${colors.mediumDarkGrey};
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

const ProductCard = ({
  product,
  wide,
  portrait,
  colours,
  xs,
  engagement,
  diamonds,
  bands,
  t,
}) => {
  const [mobileHover, setMobileHover] = useState(false)
  const [showAlternate, setShowAlternate] = useState(
    portrait || wide ? true : false
  )

  const [selectedColour, setSelectedColour] = useState(null)
  const [variant, setVariant] = useState(null)
  let productColor
  useEffect(() => {
    let selected = colours?.length > 0 ? colours[0].value : ""

    let selectedVariant = null
    if (!engagement) {
      product.variants.forEach((variant) => {
        let selectedOptions = variant.selectedOptions

        if (variant.media) {
          for (let i = 0; i < selectedOptions.length; i++) {
            let variantOption = selectedOptions[i]
            let optionValue = variantOption.value

            if (optionValue) {
              optionValue = optionValue.toLowerCase()
            }

            if (optionValue === selected) {
              selectedVariant = variant
            }

            if (!selectedVariant) {
              if (optionValue.includes(selected)) {
                selectedVariant = variant
              }
            }
          }
        }
      })
    }

    if (selectedVariant) {
      const selectedVariantColour = selectedVariant.selectedOptions.find(
        (option) =>
          option.name.toLowerCase() === "colour" ||
          option.name.toLowerCase() === "color"
      )

      if (selectedVariantColour) {
        selected = selectedVariantColour.value.toLowerCase()
      }
    }

    setVariant(selectedVariant)
    setSelectedColour(selected)
  }, [colours, product, engagement])

  let productImages
  let productImage
  let alternativeImage
  let selectedColourImage
  let variantImages = {}
  let colourImages = {}

  if (engagement) {
    productImages = product.productImages
  } else {
    productImages = product.media
  }

  if (productImages) {
    productImage = productImages[0]
  }

  if (engagement) {
    if (productImages[1]) {
      alternativeImage =
        !isPackshotImage(productImages[1]?.file.contentType) && productImages[1]
      if (selectedColour) {
        selectedColourImage = productImages.find((image) =>
          image.title.toLowerCase().includes(selectedColour)
        )
      }
    }
  } else {
    product.variants.forEach((variant) => {
      let variantImage = variant.media?.[0]?.image

      if (variantImage && isVariantImagePackshot(variantImage)) {
        let imageId = variant.media[0].id

        let selectedOptions = variant.selectedOptions

        selectedOptions.forEach((option) => {
          let optionName = option.name.toLowerCase()
          if (optionName === "colour" || optionName === "color") {
            let optionValue = option.value

            if (optionValue) {
              optionValue = optionValue.toLowerCase()
            }

            if (!colourImages[optionValue]) {
              colourImages[optionValue] = imageId
            }

            //if first image in product images is not selected as default colour image of the corresponding colour,
            //update corresponding colour image id to first product image of the same colour

            if (colourImages[optionValue]) {
              if (imageId === productImage.shopifyId) {
                colourImages[optionValue] = imageId
              }
            }
          }
        })
      }
    })

    product.variants.forEach((variant) => {
      let selectedOptions = variant.selectedOptions

      selectedOptions.forEach((option) => {
        let optionName = option.name.toLowerCase()

        if (optionName === "colour" || optionName === "color") {
          let optionValue = option.value

          if (optionValue) {
            optionValue = optionValue.toLowerCase()
          }
          if (selectedColour === "") {
            setSelectedColour(optionValue)
          }
          let imageId = colourImages[optionValue]

          if (imageId) {
            variantImages[variant.shopifyId] = imageId
          }
        }
      })
    })

    if (variant) {
      product.media.forEach((image) => {
        let imageId = image.id
        if (
          !selectedColourImage &&
          variantImages[variant.shopifyId] === imageId
        ) {
          selectedColourImage = image
        }
      })
    }
    alternativeImage = productImages.find(({ image: img }, index) => {
      if (!img) return false
      let image = null
      if (index !== 0 && !isPackshotImage(img.originalSrc) && !img.altText) {
        image = img
      }
      return image
    })
  }

  const productLink = engagement
    ? getEngagementLink(product.slug || product.urlKey)
    : product.handle === "oprah-eternity-band"
      ? getEngagementLink("oprah-eternity-ring") //special exception for oprah-eternity ring
      : getProductLinkByMaterial(
          product.urlKey || product.handle,
          selectedColour !== "" ? selectedColour : productColor,
          product.createdAt
        )

  const optionIcons = [
    {
      value: "yellow gold",
      icon: YellowIcon,
      hasOption: false,
    },
    {
      value: "rose gold",
      icon: PinkIcon,
      hasOption: false,
    },
    {
      value: "white gold",
      icon: WhiteIcon,
      hasOption: false,
    },
    {
      value: "platinum",
      icon: Platinum,
      hasOption: false,
    },
    {
      value: "yellow gold & white gold",
      icon: YellowWhiteIcon,
      hasOption: false,
    },
    {
      value: "white gold & rose gold",
      icon: WhiteRoseIcon,
      hasOption: false,
    },
    {
      value: "yellow gold & rose gold",
      icon: YellowRoseIcon,
      hasOption: false,
    },
    {
      value: "white gold & yellow gold",
      icon: WhiteYellowIcon,
      hasOption: false,
    },
    {
      value: "rose gold & yellow gold",
      icon: RoseYellowIcon,
      hasOption: false,
    },
  ]

  const allOptions = []
  let hasOptions = false

  if (engagement) {
    let productOptions = product.colourOptions.map((v) => v.toLowerCase())
    productOptions.forEach((option) => {
      allOptions.push(option.toLowerCase())
    })
  } else {
    if (product.options.length > 0) {
      product.options.forEach((option) => {
        let optionValues = option.values

        optionValues.forEach((optionValue) => {
          allOptions.push(optionValue.toLowerCase())
        })
      })
    }
  }

  optionIcons.forEach((optionIcon) => {
    if (allOptions.includes(optionIcon.value.toLowerCase())) {
      optionIcon.hasOption = true
      hasOptions = true
    }
  })

  const changeColour = (optionIcon, lifestyle = false) => {
    if (!optionIcon) {
      setSelectedColour("Yellow Gold")
      setVariant(null)

      return
    }

    if (lifestyle) {
      setShowAlternate(false)
    }

    let optionIconValue = optionIcon.value
    const productVariants = product.variants
    let selectedVariant = null

    if (optionIconValue) {
      optionIconValue = optionIconValue.toLowerCase()
    }

    if (!engagement) {
      productVariants.forEach((variant) => {
        let selectedOptions = variant.selectedOptions

        if (variant.media) {
          for (let i = 0; i < selectedOptions.length; i++) {
            let variantOption = selectedOptions[i]
            let optionValue = variantOption.value

            if (optionValue) {
              optionValue = optionValue.toLowerCase()
            }

            if (optionValue === optionIconValue) {
              selectedVariant = variant
            }

            if (!selectedVariant) {
              if (optionValue.includes(optionIconValue)) {
                selectedVariant = variant
              }
            }
          }
        }
      })
    }

    setSelectedColour(optionIconValue)
    setVariant(selectedVariant)
  }

  let isOutOfStock
  let isPreorderProduct

  if (!engagement) {
    isOutOfStock = getGlobalOutOfStock(product.tags)
    isPreorderProduct = getPreorderStatus(product.tags)
  }

  let startingPrice = 0
  let price = 0
  let currencyCode

  if (engagement) {
    if (diamonds.length > 0) {
      currencyCode = diamonds[0].priceRangeV2.minVariantPrice.currencyCode
    } else {
      currencyCode = "GBP"
    }

    startingPrice = getLocalisedPrice(
      product.startingPrice || getStartingPrice(product, diamonds, bands),
      currencyCode
    )
  } else {
    price = getLocalisedPrice(
      product.priceRangeV2.minVariantPrice.amount,
      product.priceRangeV2.minVariantPrice.currencyCode
    )
  }

  let salePrice
  let saleProduct = false
  let discountAmount = 0
  let isWinterArchiveProduct = false

  if (!engagement) {
    product.tags.forEach((tag) => {
      if (tag.includes("discount-")) {
        saleProduct = true
        discountAmount = tag.match(/\d+/g)[0]
      }

      if (tag === "winter-archives") {
        isWinterArchiveProduct = true
      }
    })
  }

  if (saleProduct) {
    salePrice =
      product.priceRangeV2.minVariantPrice.amount *
      ((100 - discountAmount) / 100)
    salePrice = getLocalisedPrice(
      salePrice,
      product.priceRangeV2.minVariantPrice.currencyCode
    )
  }

  const isFrance = getIsFrance()

  const titleFr =
    product.metafields?.length > 0 &&
    product.metafields.find((field) => field.key.trim() === "title_fr")

  let title

  if (engagement) {
    title = product.title
  } else {
    title = !isFrance ? product.title : titleFr ? titleFr.value : product.title
  }

  const renderPrice = (xs) => {
    return (
      <>
        <Price $$crossed={saleProduct ? 1 : 0} xs={xs ? 1 : 0}>
          {(engagement || product.startingFrom) && (
            <span>{t("engagement.starting_from")} </span>
          )}
          <OriginalPrice>
            {engagement ? startingPrice : price} {salePrice && <Line />}
          </OriginalPrice>
        </Price>
        {salePrice && <Price sale>{salePrice}</Price>}
      </>
    )
  }

  return (
    <CardContainer
      // style={{ display: engagement ? "none" : "block" }}
      // data-engagement={engagement}

      $regular={!portrait && !wide}
    >
      <Mobile>
        {isOutOfStock && !isPreorderProduct && (
          <OutOfStockSticker>{t("pdp.out_of_stock")}</OutOfStockSticker>
        )}
        {discountAmount > 0 && !isOutOfStock && !isPreorderProduct && (
          <DiscountSticker>{discountAmount}</DiscountSticker>
        )}
        {isPreorderProduct && (
          <OutOfStockSticker>{t("pdp.sticker.preorder")}</OutOfStockSticker>
        )}
        {product.handle === "felicity-earring" && (
          <OutOfStockSticker>Exclusive - Limited Quantity</OutOfStockSticker>
        )}
        {isWinterArchiveProduct && (
          <MessageSticker>{t("pdp.sticker.winter_archive")}</MessageSticker>
        )}
        <ProductImageLink
          to={productLink}
          onMouseEnter={() => setMobileHover(true)}
          onMouseLeave={() => setMobileHover(false)}
          aria-label={title}
        />
        <StyledImage
          image={
            engagement
              ? productImage.gatsbyImageData
              : productImage.image.gatsbyImageData
          }
          alt={title}
          $visible={1}
          loading={"eager"}
        />
        {selectedColourImage && (
          <VariantImage
            image={
              engagement
                ? selectedColourImage.gatsbyImageData
                : selectedColourImage.image.gatsbyImageData
            }
            alt={title}
            loading={"eager"}
          />
        )}
        {alternativeImage && (
          <AlternativeImage
            image={
              engagement
                ? alternativeImage.gatsbyImageData
                : alternativeImage.image.gatsbyImageData
            }
            $visible={mobileHover ? 1 : 0}
            alt={title}
            loading={"eager"}
          />
        )}
      </Mobile>
      <Desktop>
        {isOutOfStock && !isPreorderProduct && (
          <OutOfStockSticker>{t("pdp.out_of_stock")}</OutOfStockSticker>
        )}
        {discountAmount > 0 && !isOutOfStock && !isPreorderProduct && (
          <DiscountSticker>{discountAmount}</DiscountSticker>
        )}
        {isPreorderProduct && (
          <OutOfStockSticker>{t("pdp.sticker.preorder")}</OutOfStockSticker>
        )}
        {product.handle === "felicity-earring" && (
          <OutOfStockSticker>Exclusive - Limited Quantity</OutOfStockSticker>
        )}
        {isWinterArchiveProduct && (
          <MessageSticker>{t("pdp.sticker.winter_archive")}</MessageSticker>
        )}
        <ProductImageLink
          to={productLink}
          onMouseEnter={() => setShowAlternate(wide || portrait ? false : true)}
          onMouseLeave={() => setShowAlternate(wide || portrait ? true : false)}
          aria-label={title}
        />
        {wide ? (
          <WideImageContainer>
            <ImageContainer wide>
              <StyledImage
                image={
                  engagement
                    ? productImage.gatsbyImageData
                    : productImage.image.gatsbyImageData
                }
                alt={title}
                loading={"eager"}
                $visible={1}
              />
              {selectedColourImage && (
                <VariantImage
                  image={
                    engagement
                      ? selectedColourImage.gatsbyImageData
                      : selectedColourImage.image.gatsbyImageData
                  }
                  loading={"eager"}
                  alt={title}
                />
              )}
            </ImageContainer>
            {alternativeImage && (
              <AlternativeImage
                image={
                  engagement
                    ? alternativeImage.gatsbyImageData
                    : alternativeImage.image.gatsbyImageData
                }
                $visible={showAlternate ? 1 : 0}
                loading={"eager"}
                alt={title}
              />
            )}
          </WideImageContainer>
        ) : (
          <ImageContainer>
            <StyledImage
              image={
                engagement ? productImage.portrait : productImage.image.portrait
              }
              alt={title}
              $visible={1}
              loading={"eager"}
            />
            {selectedColourImage && (
              <VariantImage
                image={
                  engagement
                    ? selectedColourImage.portrait
                    : selectedColourImage.image.portrait
                }
                alt={title}
                loading={"eager"}
              />
            )}
            {alternativeImage && (
              <AlternativeImage
                image={
                  engagement
                    ? alternativeImage.portrait
                    : alternativeImage.image.portrait
                }
                $visible={showAlternate ? 1 : 0}
                alt={title}
                loading={"eager"}
              />
            )}
          </ImageContainer>
        )}
      </Desktop>
      <ProductDetailsBlock xs={xs}>
        <ProductDetails xs={xs}>
          <TitleSection>
            <Title>{title}</Title>
            <PriceSection $desktop>{renderPrice(xs)}</PriceSection>
          </TitleSection>
          <div>
            {hasOptions && (
              <Icons xs={xs}>
                {optionIcons.map((optionIcon, index) => {
                  return (
                    optionIcon.hasOption && (
                      <IconContainer
                        key={index}
                        $isSelected={selectedColour === optionIcon.value}
                        xs={xs}
                        onClick={() =>
                          changeColour(optionIcon, wide || portrait)
                        }
                      >
                        <img src={optionIcon.icon} alt={optionIcon.value} />
                      </IconContainer>
                    )
                  )
                })}
              </Icons>
            )}
          </div>
          <PriceSection>{renderPrice(xs)}</PriceSection>
        </ProductDetails>
      </ProductDetailsBlock>
    </CardContainer>
  )
}

export default withTranslation()(ProductCard)
